import React, { useRef, useState, useEffect } from 'react';
import { motion } from 'framer-motion';

interface CBNode {
  id: string;
  label: string;
  xPct: number;
  yPct: number;
  status: 'active' | 'processing';
  size: 'sm' | 'md' | 'lg';
}

interface CBConnection {
  from: string;
  to: string;
  bidirectional?: boolean;
  delay?: number;
}

interface ProjectConfig {
  color: string;
  nodes: CBNode[];
  connections: CBConnection[];
}

const CONFIGS: Record<string, ProjectConfig> = {
  'cb-rejection': {
    color: '#ff2d00',
    nodes: [
      { id: 'source', label: 'Bank API',        xPct: 10, yPct: 50, status: 'active',     size: 'md' },
      { id: 'fetch',  label: 'Fetch Engine',     xPct: 36, yPct: 28, status: 'processing', size: 'sm' },
      { id: 'parse',  label: 'Parser',           xPct: 36, yPct: 72, status: 'active',     size: 'sm' },
      { id: 'filter', label: 'CB Filter',        xPct: 62, yPct: 50, status: 'processing', size: 'lg' },
      { id: 'dash',   label: 'Dashboard',        xPct: 88, yPct: 28, status: 'active',     size: 'sm' },
      { id: 'rep',    label: 'Daily Report',     xPct: 88, yPct: 72, status: 'active',     size: 'sm' },
    ],
    connections: [
      { from: 'source', to: 'fetch',  delay: 0   },
      { from: 'source', to: 'parse',  delay: 0.2 },
      { from: 'fetch',  to: 'filter', delay: 0.4 },
      { from: 'parse',  to: 'filter', delay: 0.6 },
      { from: 'filter', to: 'dash',   delay: 0.8 },
      { from: 'filter', to: 'rep',    delay: 1.0 },
    ],
  },
  'branch-locator': {
    color: '#ff2d00',
    nodes: [
      { id: 'gps',    label: 'GPS Input',  xPct: 10, yPct: 50, status: 'active',     size: 'md' },
      { id: 'router', label: 'Router',     xPct: 36, yPct: 50, status: 'processing', size: 'md' },
      { id: 'db',     label: 'Branch DB',  xPct: 62, yPct: 28, status: 'active',     size: 'lg' },
      { id: 'geo',    label: 'Geo Engine', xPct: 62, yPct: 72, status: 'processing', size: 'sm' },
      { id: 'map',    label: 'Live Map',   xPct: 88, yPct: 50, status: 'active',     size: 'lg' },
    ],
    connections: [
      { from: 'gps',    to: 'router', delay: 0,    bidirectional: true },
      { from: 'router', to: 'db',     delay: 0.3               },
      { from: 'router', to: 'geo',    delay: 0.5               },
      { from: 'db',     to: 'map',    delay: 0.75, bidirectional: true },
      { from: 'geo',    to: 'map',    delay: 0.95              },
    ],
  },
  'gold-loan-calculator': {
    color: '#ff2d00',
    nodes: [
      { id: 'gold',   label: 'Gold Input',  xPct: 10, yPct: 33, status: 'active',     size: 'sm' },
      { id: 'weight', label: 'Weight',      xPct: 10, yPct: 67, status: 'active',     size: 'sm' },
      { id: 'rate',   label: 'Market Rate', xPct: 38, yPct: 50, status: 'processing', size: 'md' },
      { id: 'engine', label: 'Calc Engine', xPct: 64, yPct: 50, status: 'processing', size: 'lg' },
      { id: 'elig',   label: 'Eligibility', xPct: 90, yPct: 33, status: 'active',     size: 'sm' },
      { id: 'amount', label: 'Loan Amount', xPct: 90, yPct: 67, status: 'active',     size: 'sm' },
    ],
    connections: [
      { from: 'gold',   to: 'rate',   delay: 0               },
      { from: 'weight', to: 'rate',   delay: 0.2             },
      { from: 'rate',   to: 'engine', delay: 0.45, bidirectional: true },
      { from: 'engine', to: 'elig',   delay: 0.7             },
      { from: 'engine', to: 'amount', delay: 0.9             },
    ],
  },
};

const BASE_SIZES = { sm: 36, md: 50, lg: 64 };
const DESIGN_W = 600;
const DESIGN_H = 338;
const PULSE_SPEED = 2.2;

const ProjectCircuitBoard: React.FC<{ id: string }> = ({ id }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [dims, setDims] = useState<{ w: number; h: number } | null>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => setDims({ w: el.offsetWidth, h: el.offsetHeight });
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const cfg = CONFIGS[id];
  if (!cfg) return <div ref={containerRef} className='absolute inset-0' />;

  // Scale factor capped at 1.4 so nodes don't become enormous on large screens
  const scale = dims ? Math.min(1.4, Math.min(dims.w / DESIGN_W, dims.h / DESIGN_H)) : 1;
  const nodeSizes = {
    sm: Math.max(28, BASE_SIZES.sm * scale),
    md: Math.max(38, BASE_SIZES.md * scale),
    lg: Math.max(48, BASE_SIZES.lg * scale),
  };

  const nodeMap = new Map(cfg.nodes.map(n => [n.id, n]));

  const px = (node: CBNode) => ({
    x: (node.xPct / 100) * (dims?.w ?? DESIGN_W),
    y: (node.yPct / 100) * (dims?.h ?? DESIGN_H),
  });

  // Half the rendered node box + a small gap, so traces meet the node edge
  // (not its center) and read as clean circuit connections.
  const edgeOffset = (node: CBNode) => nodeSizes[node.size] / 2 + 4;

  const buildPath = (from: CBNode, to: CBNode) => {
    const f = px(from);
    const t = px(to);
    const fo = edgeOffset(from);
    const to_ = edgeOffset(to);
    const dx = t.x - f.x;
    const dy = t.y - f.y;
    if (Math.abs(dx) >= Math.abs(dy)) {
      // Horizontal-dominant: leave/enter on left or right edge at vertical center.
      const startX = f.x + (dx > 0 ? fo : -fo);
      const endX = t.x + (dx > 0 ? -to_ : to_);
      const midX = f.x + dx / 2;
      return `M ${startX} ${f.y} H ${midX} V ${t.y} H ${endX}`;
    }
    // Vertical-dominant: leave/enter on top or bottom edge at horizontal center.
    const startY = f.y + (dy > 0 ? fo : -fo);
    const endY = t.y + (dy > 0 ? -to_ : to_);
    const midY = f.y + dy / 2;
    return `M ${f.x} ${startY} V ${midY} H ${t.x} V ${endY}`;
  };

  const safeId = id.replace(/[^a-z0-9]/g, '');
  const filterId = `pcb-glow-${safeId}`;
  const patternId = `pcb-grid-${safeId}`;

  return (
    <div ref={containerRef} className='absolute inset-0'>
      {dims && (
        <>
          {/* SVG: grid dots + connection traces */}
          <svg className='absolute inset-0 w-full h-full' aria-hidden='true'>
            <defs>
              <pattern id={patternId} width={22} height={22} patternUnits='userSpaceOnUse'>
                <circle cx={11} cy={11} r={0.75} fill={cfg.color} fillOpacity={0.12} />
              </pattern>
              <filter id={filterId} x='-80%' y='-80%' width='260%' height='260%'>
                <feGaussianBlur stdDeviation={2.5} result='blur' />
                <feMerge>
                  <feMergeNode in='blur' />
                  <feMergeNode in='SourceGraphic' />
                </feMerge>
              </filter>
            </defs>

            {/* Dot grid */}
            <rect width='100%' height='100%' fill={`url(#${patternId})`} />

            {/* Connections */}
            {cfg.connections.map((conn, i) => {
              const fromNode = nodeMap.get(conn.from);
              const toNode = nodeMap.get(conn.to);
              if (!fromNode || !toNode) return null;
              const path = buildPath(fromNode, toNode);

              return (
                <g key={`c${i}`}>
                  {/* Base trace */}
                  <motion.path
                    d={path}
                    fill='none'
                    stroke={cfg.color}
                    strokeWidth={1.5}
                    strokeOpacity={0.38}
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 0.9, delay: 0.5 + (conn.delay ?? 0), ease: 'easeOut' }}
                  />
                  {/* Forward pulse dot */}
                  <motion.path
                    d={path}
                    fill='none'
                    stroke={cfg.color}
                    strokeWidth={3}
                    strokeOpacity={0.9}
                    strokeLinecap='round'
                    pathLength={1}
                    strokeDasharray='0.06 0.94'
                    filter={`url(#${filterId})`}
                    initial={{ strokeDashoffset: 1 }}
                    animate={{ strokeDashoffset: -1 }}
                    transition={{
                      duration: PULSE_SPEED,
                      repeat: Infinity,
                      ease: 'linear',
                      delay: 1.4 + (conn.delay ?? 0),
                    }}
                  />
                  {/* Reverse pulse for bidirectional */}
                  {conn.bidirectional && (
                    <motion.path
                      d={path}
                      fill='none'
                      stroke={cfg.color}
                      strokeWidth={3}
                      strokeOpacity={0.6}
                      strokeLinecap='round'
                      pathLength={1}
                      strokeDasharray='0.06 0.94'
                      filter={`url(#${filterId})`}
                      initial={{ strokeDashoffset: -1 }}
                      animate={{ strokeDashoffset: 1 }}
                      transition={{
                        duration: PULSE_SPEED,
                        repeat: Infinity,
                        ease: 'linear',
                        delay: 1.4 + (conn.delay ?? 0) + PULSE_SPEED / 2,
                      }}
                    />
                  )}
                </g>
              );
            })}
          </svg>

          {/* Nodes */}
          {cfg.nodes.map((node, i) => {
            const { x, y } = px(node);
            const sz = nodeSizes[node.size];
            const isProcessing = node.status === 'processing';

            return (
              <motion.div
                key={node.id}
                className='absolute'
                // Center the box on (x, y) via left/top — NOT a CSS transform,
                // because framer-motion's scale animation overwrites `transform`
                // and would otherwise drop the centering (shifting the box by sz/2).
                style={{
                  left: x - sz / 2,
                  top: y - sz / 2,
                  width: sz,
                  height: sz,
                }}
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.7 + i * 0.1, type: 'spring', stiffness: 220, damping: 18 }}
              >
                {/* Outer glow ring */}
                <motion.div
                  className='absolute rounded-lg'
                  style={{
                    width: sz + (isProcessing ? 18 : 12),
                    height: sz + (isProcessing ? 18 : 12),
                    left: '50%',
                    top: '50%',
                    transform: 'translate(-50%,-50%)',
                    border: `1px solid ${cfg.color}`,
                    boxShadow: `0 0 14px ${cfg.color}45`,
                  }}
                  animate={{ opacity: isProcessing ? [0.15, 0.5, 0.15] : [0.2, 0.5, 0.2] }}
                  transition={{
                    duration: isProcessing ? 1.4 : 2.8,
                    repeat: Infinity,
                    ease: 'easeInOut',
                    delay: i * 0.18,
                  }}
                />

                {/* Main node box — fills the wrapper, so its center sits exactly
                    on (x, y) where the traces connect. */}
                <div
                  className='absolute inset-0 flex items-center justify-center rounded-lg'
                  style={{
                    backgroundColor: `${cfg.color}10`,
                    border: `1.5px solid ${cfg.color}65`,
                  }}
                >
                  {/* Corner pads */}
                  {([-1, 1] as const).map(cx =>
                    ([-1, 1] as const).map(cy => (
                      <div
                        key={`${cx}${cy}`}
                        className='absolute rounded-full'
                        style={{
                          width: 4,
                          height: 4,
                          backgroundColor: cfg.color,
                          opacity: 0.55,
                          left: cx > 0 ? 'auto' : 4,
                          right: cx > 0 ? 4 : 'auto',
                          top: cy > 0 ? 'auto' : 4,
                          bottom: cy > 0 ? 4 : 'auto',
                        }}
                      />
                    ))
                  )}

                  {/* Center indicator */}
                  <motion.div
                    className='rounded-full'
                    style={{
                      width: Math.max(6, sz * 0.22),
                      height: Math.max(6, sz * 0.22),
                      backgroundColor: cfg.color,
                    }}
                    animate={{ opacity: isProcessing ? [0.3, 1, 0.3] : [0.55, 1, 0.55] }}
                    transition={{
                      duration: isProcessing ? 1.1 : 2.2,
                      repeat: Infinity,
                      ease: 'easeInOut',
                      delay: i * 0.15,
                    }}
                  />
                </div>

                {/* Label — floated below the box without affecting its position */}
                <div
                  className='absolute whitespace-nowrap font-mono leading-none text-center'
                  style={{
                    left: '50%',
                    top: '100%',
                    marginTop: 6,
                    transform: 'translateX(-50%)',
                    fontSize: Math.max(7, 8 * scale),
                    color: cfg.color,
                    opacity: 0.72,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                  }}
                >
                  {node.label}
                </div>
              </motion.div>
            );
          })}

          {/* Subtle sweep overlay */}
          <motion.div
            className='absolute inset-0 pointer-events-none'
            style={{
              background: `linear-gradient(90deg, transparent 0%, ${cfg.color}08 50%, transparent 100%)`,
            }}
            animate={{ x: ['-100%', '200%'] }}
            transition={{ duration: 5, repeat: Infinity, ease: 'linear', repeatDelay: 3 }}
          />
        </>
      )}
    </div>
  );
};

export default ProjectCircuitBoard;
